import React, { useEffect, useState, useCallback, useContext } from 'react';
import clsx from 'clsx';
import { useTranslation } from "react-i18next";
import {
    Card,
    CardContent,
    Tooltip,
    Box,
    makeStyles,
} from '@material-ui/core';
import ServerTable from 'components/ServerTable';
import SecureFetchContext from 'context/SecureFetchContext';
import getApiRoute from 'routes/apiRoutes';
import AppContext from 'context/AppContext';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import CodPrice from 'components/CodPrice';
import {createUrlQuery, formatDate, getShipperIcon, isUser, isAdmin, openNewTab} from 'helpers';

const useStyles = makeStyles((theme) => ({
    root: {
        '& table .MuiTableCell-sizeSmall': {
            padding: '6px 4px'
        }
    },
    checkbox: {
        padding: 0
    }
}));

const PackagePaidTable = ({ className, ...rest }) => {
    const classes = useStyles();
    const [rowsData, setRowsData] = useState({ rows: [], count: 0 });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const secureFetch = useContext(SecureFetchContext);
    const { appValues } = useContext(AppContext);
 
    const loadPackages = useCallback((pConditions = null) => {
        setLoading(true);
        secureFetch( getApiRoute( isUser(appValues.user) ? 'package_paid' : 'admin_package_paid' ) + '?' + createUrlQuery(pConditions) ).then(json => {
            setLoading(false);
            setRowsData(json);
        })
    }, [secureFetch, appValues.user])

    const exportData = [
        {
            'name': t('button.export_csv'),
            'url': getApiRoute('package_paid', ['export'], isAdmin(appValues.user)),
        },
        
    ];

    if (isUser(appValues.user)) {
        exportData.push({
            'name': t('button.export_sepa'),
            'url': getApiRoute('package_paid', ['export', 'sepa'], isAdmin(appValues.user)),
        });
        exportData.push({
            'name': t('button.export_xml'),
            'url': getApiRoute('package_paid', ['export', 'xml'], isAdmin(appValues.user)),
        });
        exportData.push({
            'name': t('button.export_excel'),
            'url': getApiRoute('package_paid', ['export', 'excel'], isAdmin(appValues.user)),
        })
    }

    const settings = {
        rows: rowsData.rows,
        rowsLength: rowsData.count,
        loading: loading,
        colOrder: {
            order: 'desc',
            order_by: 'invoice_date'
        },
        loadFilterDate: {
            invoice_date_from: new Date(new Date().setDate(new Date().getDate() - 30)),
            invoice_date_to: new Date(),
        },
        handleTableUpdate: loadPackages,
        headCells: [
            { id: 'id', numeric: false, label: t('label.id'), search: false, hide: true, disableOrder: true },
            { id: 'shipper', numeric: false, label: t('label.shipper'), search: true, searchType: 'select', selectChoices: appValues.shippers, render: (data) => getShipperIcon(data) },
            { id: 'invoice_date', numeric: false, label: t('label.invoice_date'), search: true, searchType: 'dateRange', render: (data) => formatDate(data.invoice_date) },
            { id: 'invoice_number', numeric: false, label: t('label.invoice_number'), search: true },
            { id: 'reference_number', numeric: false, label: t('label.reference_number'), search: true },
            { id: 'cod_reference', numeric: false, label: t('label.cod_reference'), search: true },
            { id: 'tracking_number', numeric: false, label: t('label.tracking_number'), search: true },
            { id: 'receiver', numeric: false, label: t('label.receiver'), search: true, disableOrder: true },
            { id: 'cod_payment', numeric: true, label: t('label.cod_price'), filterName: t('label.payment_type'), search: true, searchType: 'select', selectChoices: appValues.paymentTypes.map(type => {return {...type, id: type.value}}), render: (data) => <CodPrice data={data} /> },
            { id: 'actions', numeric: false, label: t('label.actions'), disableOrder: true, search: false, render: (data) => {
                return (
                    <Box display="flex">
                        <Tooltip title={t('tooltip.detail')} aria-label={t('tooltip.detail')}>
                            <IconButton
                                aria-label={t('tooltip.detail')}
                                className={classes.rowButtons}
                                onClick={() => openNewTab("/app/package/detail/" + data.id)}
                            >
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            } },
        ],
        addFilter: {
            states: true,
            cod_currency_code: true,
            cod_sum: true,
        },
        topRightButtons: () => {
            return (null)
        },
        exportData: exportData
    };

    if ( isAdmin(appValues.user) ) {
        settings.headCells.splice(2, 0, { id: 'user', numeric: false, label: t('label.eshop_name'), search: true, searchType: 'select', selectChoices: users, render: (data) => data.eshop});
    }

    useEffect(() => {
        if ( isAdmin(appValues.user) ) {
            secureFetch( getApiRoute( 'admin_user' ) ).then(json => {
                setUsers(json.map(user => {
                    return {
                        id: user.id,
                        name: user.eshop || ''
                    }
                }));
            })
        }
    }, [appValues.user, secureFetch]);


    return (
        <Card
            className={clsx(classes.root, className)}
            {...rest}
        >
            <CardContent>
                <ServerTable settings={settings} />
            </CardContent>
        </Card>
    );
}

export default PackagePaidTable;